import React, { useState, useEffect } from "react";
import { useHierarchy } from "../contexts/HierarchyContext.jsx";
import { apiGet, apiPut } from "../api/client.js";

export default function Settings({ navigate }) {
  const { orgId, tenantId, subTenantId, currentSubTenant, refreshSubTenants } = useHierarchy();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (!orgId || !tenantId || !subTenantId) return;
    apiGet(`/api/v1/orgs/${orgId}/tenants/${tenantId}/subtenants/${subTenantId}`)
      .then((sub) => {
        setForm({
          sandyWsUrl: sub.sandyWsUrl || "ws://localhost:3010/ws",
          sandyTenantId: sub.sandyConfig?.sandyTenantId || "",
          mode: sub.sandyConfig?.mode || "text",
          testTimeout: sub.sandyConfig?.testTimeout || 480,
          maxConcurrent: sub.sandyConfig?.maxConcurrent || 2,
          patientModel: sub.sandyConfig?.patientModel || "gemini-2.5-flash",
          verificationModel: sub.sandyConfig?.verificationModel || "gemini-2.5-flash",
          patientTemperature: sub.sandyConfig?.patientTemperature || 0.7,
          gcpProject: sub.sandyConfig?.gcpProject || "",
          gcpLocation: sub.sandyConfig?.gcpLocation || "",
          // Clinic config
          location: sub.config?.location || "",
          phone: sub.config?.phone || "",
          afterHoursPhone: sub.config?.afterHoursPhone || "",
          timezone: sub.config?.timezone || "America/New_York",
        });
      })
      .catch((err) => alert("Failed to load settings: " + err.message));
  }, [orgId, tenantId, subTenantId]);

  const setField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiPut(`/api/v1/orgs/${orgId}/tenants/${tenantId}/subtenants/${subTenantId}`, {
        sandyWsUrl: form.sandyWsUrl,
        sandyConfig: {
          sandyTenantId: form.sandyTenantId,
          mode: form.mode,
          testTimeout: parseInt(form.testTimeout, 10) || 480,
          maxConcurrent: parseInt(form.maxConcurrent, 10) || 2,
          patientModel: form.patientModel,
          verificationModel: form.verificationModel,
          patientTemperature: parseFloat(form.patientTemperature) || 0.7,
          gcpProject: form.gcpProject,
          gcpLocation: form.gcpLocation,
        },
        config: {
          location: form.location,
          phone: form.phone,
          afterHoursPhone: form.afterHoursPhone,
          timezone: form.timezone,
        },
      });
      setSaved(true);
      refreshSubTenants();
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    // Connectivity check using agent-runtime's gateway-level ping/pong.
    // Avoids `CallClient.connect()` because it sends a legacy
    // {type:"configure",...} envelope that agent-runtime's zod schema
    // rejects (the new wire format is keyed on `kind`, not `type`).
    // ping/pong is the cheapest frame the gateway accepts and round-trips
    // without needing a full session.start config payload.
    const TIMEOUT_MS = 5000;
    const nonce = `test-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

    let ws;
    try {
      const result = await new Promise((resolve, reject) => {
        const timeoutId = setTimeout(
          () => reject(new Error(`Connection timed out after ${TIMEOUT_MS / 1000} seconds`)),
          TIMEOUT_MS
        );
        try {
          ws = new WebSocket(form.sandyWsUrl);
        } catch (err) {
          clearTimeout(timeoutId);
          reject(new Error(`Invalid WebSocket URL: ${err.message || form.sandyWsUrl}`));
          return;
        }
        ws.onopen = () => {
          try {
            ws.send(JSON.stringify({ kind: "ping", nonce }));
          } catch (err) {
            clearTimeout(timeoutId);
            reject(new Error(`Failed to send ping: ${err.message}`));
          }
        };
        ws.onmessage = (ev) => {
          let msg;
          try { msg = JSON.parse(ev.data); }
          catch { return; }
          if (msg?.kind === "pong" && msg.nonce === nonce) {
            clearTimeout(timeoutId);
            resolve("pong");
          }
        };
        ws.onerror = () => {
          clearTimeout(timeoutId);
          reject(new Error(`WebSocket error connecting to ${form.sandyWsUrl}`));
        };
        ws.onclose = (ev) => {
          clearTimeout(timeoutId);
          reject(new Error(
            `WebSocket closed before pong (code=${ev.code}${ev.reason ? ` reason=${ev.reason}` : ""})`
          ));
        };
      });
      setTestResult({ ok: true, message: `Connected — runtime responded to ping (${result})` });
    } catch (err) {
      setTestResult({ ok: false, message: err.message || `Failed to connect to ${form.sandyWsUrl}` });
    } finally {
      try { ws?.close(1000, "test-complete"); } catch { /* ignore */ }
      setTesting(false);
    }
  };

  if (!form) return <div style={s.loading}>Loading settings...</div>;

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.title}>Connection Settings</h1>
        <div style={s.subTitle}>{currentSubTenant?.name || "—"}</div>
      </div>

      <div style={s.body}>
        {/* Sandy Connection */}
        <Section title="Sandy Agent Server" hint="Connection parameters used when running tests and manual calls against this sub-tenant's Sandy instance.">
          <div style={s.row}>
            <Field label="Sandy WebSocket URL *" hint="The full WebSocket URL for Sandy's agent server" flex={3}>
              <input style={s.input} value={form.sandyWsUrl} onChange={(e) => setField("sandyWsUrl", e.target.value)} placeholder="ws://localhost:3010/ws" />
            </Field>
            <Field label="Test" flex={1}>
              <button
                style={testing ? s.testBtnDisabled : s.testBtn}
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? "Testing..." : "Test Connection"}
              </button>
            </Field>
          </div>
          {testResult && (
            <div style={testResult.ok ? s.testOk : s.testFail}>
              {testResult.ok ? "✓" : "✗"} {testResult.message}
            </div>
          )}

          <Field label="Sandy Tenant ID" hint="The tenant ID sent in Sandy's 'configure' WebSocket message. Leave empty to use this sub-tenant's MongoDB _id. Set this to match Sandy's internal tenant config (e.g., 'default', 'future-orthopedics').">
            <input style={s.input} value={form.sandyTenantId} onChange={(e) => setField("sandyTenantId", e.target.value)} placeholder={subTenantId + " (default: MongoDB _id)"} />
          </Field>

          <div style={s.row}>
            <Field label="Mode" flex={1}>
              <select style={s.input} value={form.mode} onChange={(e) => setField("mode", e.target.value)}>
                <option value="text">Text</option>
                <option value="voice">Voice</option>
              </select>
            </Field>
            <Field label="Test Timeout (seconds)" flex={1}>
              <input type="number" style={s.input} value={form.testTimeout} onChange={(e) => setField("testTimeout", e.target.value)} />
            </Field>
            <Field label="Max Concurrent Tests" flex={1}>
              <input type="number" style={s.input} value={form.maxConcurrent} onChange={(e) => setField("maxConcurrent", e.target.value)} min={1} max={10} />
            </Field>
          </div>
        </Section>

        {/* Gemini / AI Config */}
        <Section title="AI Models" hint="Models used for the synthetic patient simulator and post-call verification scoring.">
          <div style={s.row}>
            <Field label="Patient Simulator Model" flex={2}>
              <input style={s.input} value={form.patientModel} onChange={(e) => setField("patientModel", e.target.value)} placeholder="gemini-2.5-flash" />
            </Field>
            <Field label="Temperature" flex={1}>
              <input type="number" style={s.input} value={form.patientTemperature} onChange={(e) => setField("patientTemperature", e.target.value)} min={0} max={1} step={0.1} />
            </Field>
          </div>
          <Field label="Verification Model">
            <input style={s.input} value={form.verificationModel} onChange={(e) => setField("verificationModel", e.target.value)} placeholder="gemini-2.5-flash" />
          </Field>
          <div style={s.row}>
            <Field label="GCP Project" hint="Leave empty to use server env var" flex={1}>
              <input style={s.input} value={form.gcpProject} onChange={(e) => setField("gcpProject", e.target.value)} placeholder="(from env)" />
            </Field>
            <Field label="GCP Location" hint="Leave empty to use server env var" flex={1}>
              <input style={s.input} value={form.gcpLocation} onChange={(e) => setField("gcpLocation", e.target.value)} placeholder="(from env)" />
            </Field>
          </div>
        </Section>

        {/* Clinic Config */}
        <Section title="Clinic Details" hint="Practice information displayed in notifications and appointment confirmations.">
          <Field label="Location / Address">
            <input style={s.input} value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="6545 Main St, Suite 400, Dallas, TX 75001" />
          </Field>
          <div style={s.row}>
            <Field label="Phone" flex={1}>
              <input style={s.input} value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="555-100-1001" />
            </Field>
            <Field label="After-Hours Phone" flex={1}>
              <input style={s.input} value={form.afterHoursPhone} onChange={(e) => setField("afterHoursPhone", e.target.value)} placeholder="555-100-1099" />
            </Field>
          </div>
          <Field label="Practice Timezone" hint="All appointment slots, scheduling, and time displays use this timezone.">
            <select style={s.input} value={form.timezone} onChange={(e) => setField("timezone", e.target.value)}>
              <optgroup label="US Eastern">
                <option value="America/New_York">Eastern Time (America/New_York)</option>
              </optgroup>
              <optgroup label="US Central">
                <option value="America/Chicago">Central Time (America/Chicago)</option>
              </optgroup>
              <optgroup label="US Mountain">
                <option value="America/Denver">Mountain Time (America/Denver)</option>
                <option value="America/Phoenix">Arizona (America/Phoenix — no DST)</option>
              </optgroup>
              <optgroup label="US Pacific">
                <option value="America/Los_Angeles">Pacific Time (America/Los_Angeles)</option>
              </optgroup>
              <optgroup label="US Alaska & Hawaii">
                <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
              </optgroup>
              <optgroup label="Other">
                <option value="UTC">UTC</option>
                <option value="Europe/London">London (Europe/London)</option>
                <option value="Europe/Berlin">Berlin (Europe/Berlin)</option>
                <option value="Asia/Kolkata">India (Asia/Kolkata)</option>
                <option value="Asia/Tokyo">Tokyo (Asia/Tokyo)</option>
                <option value="Australia/Sydney">Sydney (Australia/Sydney)</option>
              </optgroup>
            </select>
          </Field>
        </Section>

        {/* Save */}
        <div style={s.actions}>
          {saved && <span style={s.savedBadge}>Saved</span>}
          <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionTitle}>{title}</div>
      {hint && <div style={s.sectionHint}>{hint}</div>}
      {children}
    </div>
  );
}

function Field({ label, hint, flex, children }) {
  return (
    <div style={{ ...s.field, ...(flex ? { flex } : {}) }}>
      <label style={s.label}>{label}</label>
      {hint && <div style={s.fieldHint}>{hint}</div>}
      {children}
    </div>
  );
}

const s = {
  loading: { padding: 60, textAlign: "center", color: "#999", fontSize: 14 },
  header: { padding: "20px 32px", borderBottom: "1px solid #e5e5e5" },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em" },
  subTitle: { fontSize: 13, color: "#999", marginTop: 4 },
  body: { padding: "24px 32px", maxWidth: 760 },
  section: { marginBottom: 28, paddingBottom: 24, borderBottom: "1px solid #f0f0f0" },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: "#0f0f0f", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.03em" },
  sectionHint: { fontSize: 12, color: "#999", marginBottom: 14, lineHeight: 1.4 },
  row: { display: "flex", gap: 12, alignItems: "flex-end" },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, fontWeight: 500, color: "#666", marginBottom: 5 },
  fieldHint: { fontSize: 11, color: "#999", marginBottom: 5, marginTop: -2, lineHeight: 1.3 },
  input: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 6,
    fontSize: 13, outline: "none", color: "#0f0f0f", background: "#fff",
    boxSizing: "border-box", fontFamily: "inherit",
  },
  testBtn: {
    width: "100%", padding: "8px 12px", background: "#fff", color: "#0f0f0f",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
    marginTop: 0,
  },
  testBtnDisabled: {
    width: "100%", padding: "8px 12px", background: "#f5f5f5", color: "#999",
    border: "1px solid #e5e5e5", borderRadius: 6, fontSize: 13, fontWeight: 500,
    cursor: "wait",
  },
  testOk: {
    padding: "8px 12px", background: "#ecfdf5", color: "#059669",
    borderRadius: 6, fontSize: 12, fontWeight: 500, marginBottom: 14,
  },
  testFail: {
    padding: "8px 12px", background: "#fef2f2", color: "#dc2626",
    borderRadius: 6, fontSize: 12, fontWeight: 500, marginBottom: 14,
  },
  actions: {
    display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center",
    padding: "20px 0 40px",
  },
  savedBadge: {
    fontSize: 12, color: "#059669", background: "#ecfdf5",
    padding: "4px 10px", borderRadius: 10, fontWeight: 500,
  },
  saveBtn: {
    padding: "9px 24px", background: "#0f0f0f", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 13, fontWeight: 500,
  },
};
